import React, { useContext, useEffect, useState } from "react";
import { Button, Modal, Form, DatePicker, InputNumber, Select, Input, Row, Col, Tooltip } from "antd";
import { listExam, createExam, deleteExam, getBelts, ListBranch, updateExam } from "../../constants/API/API";
import moment from "moment";
import useApi from "../../hooks/useApi";
import callApi from "../../constants/API/CallApi";
import { Severty, ShowToast } from "../../helper/toast";
import { useNavigate } from "react-router";
import { EditOutlined, EyeOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { AppStateContext } from "../../context/AppContext";
import DeleteRecord from "../../components/DeleteRecord";
import CustomTable from "../../components/CustomTable";
import useFilter from "../../hooks/useFilter";

const ExamAdmin = () => {
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Exam Management");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [examModal, setExamModal] = useState(false);
  const [exam, setExam] = useState();
  const [totalDocs, setTotalDocs] = useState(0);
  const [filters, setFilters] = useFilter({
    name: "",
    startDate: null,
    endDate: null,
  });

  const naviagte = useNavigate();

  const columns = [
    {
      title: "Exam Name",
      dataIndex: "name",
      key: "name",
      render: (name) => name || "-",
    },
    {
      title: "Belt",
      dataIndex: ["beltId", "name"],
      key: "belt",
      render: (_, record) =>
        record?.beltId?.map((item) => item?.name)?.join(" , ") || "-",
    },
    { title: "Branch", dataIndex: ["branchId", "name"], key: "branch" },
    {
      title: "Exam Date",
      dataIndex: "examDate",
      render: (_, record) => (record?.date ? moment(record.date).format("ll") : "-"),
    },
    {
      title: "Total Capacity",
      dataIndex: "totalCapacity",
      key: "totalCapacity",
      render: (val, record) => (record?.totalCapacity ?? record?.capacity ?? "-"),
    },
    {
      title: "Remaining Capacity",
      dataIndex: "remainingCapacity",
      key: "remainingCapacity",
      render: (val, record) => (record?.remainingCapacity != null ? record.remainingCapacity : "-"),
    },
    {
      title: "Reserved Exam Spots",
      dataIndex: "reservedSpots",
      key: "reservedSpots",
      render: (val, record) => (record?.reservedSpots ?? (record?.registeredStudents || []).length ?? 0),
    },
    {
      title: "Total Eligible Students",
      dataIndex: "totalEligibleStudents",
      key: "totalEligibleStudents",
      render: (val, record) => (record?.totalEligibleStudents ?? 0),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, rec) => (
        <div className="d-flex align-items-center gap-2">
          <Tooltip title="Edit">
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => {
                setExam(rec);
                setExamModal(true);
              }}
            />
          </Tooltip>

          <Tooltip title="View">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => {
                naviagte(`/exam/view/${rec._id}?branchId=${rec?.branchId?._id}`);
              }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <div>
              <DeleteRecord
                _id={rec._id}
                callback={() => deleteExam(rec._id)}
                setLoading={setLoading}
                setRefresh={setRefresh}
                trigger={true}
              />
            </div>
          </Tooltip>
        </div>
      ),
    },
  ];

  const { loading: isExamLoading } = useApi({
    callback: listExam,
    payload: filters,
    dependencies: [refresh, filters],
    shouldCallApi: true,
    setLoading,
    onSuccess: ({ data }) => {
      setLoading(false);
      setList(data || []);
      setTotalDocs((data || []).length);
    },
    onError: (err) => {
      setLoading(false);
      ShowToast(err, Severty.ERROR);
    },
  });

  return (
    <>
      <Button onClick={() => naviagte(-1)} className="main-new-bacbuttonn mb-3">
        <ArrowLeftOutlined /> Back
      </Button>

      <CustomTable
        data={list}
        loading={loading || !!isExamLoading}
        columns={columns}
        filters={filters}
        setFilters={setFilters}
        fields={{ range: true, search: true }}
        searchField={["name"]}
        searchPlaceholder="Search by Exam Name"
        customView={
          <Button type="primary" onClick={() => setExamModal(true)}>
            New Exam
          </Button>
        }
        totalDocs={totalDocs || list.length}
      />

      {examModal && (
        <ExamModal
          record={exam}
          refresh={() => setRefresh((x) => !x)}
          hide={() => {
            setExamModal(false);
            setExam(null);
          }}
        />
      )}
    </>
  );
};

export default ExamAdmin;

const ExamModal = ({ refresh, hide, record }) => {
  const [loading, setLoading] = useState(false);
  const [belts, setBelts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [form] = Form.useForm();

  const handleOk = async (values) => {
    setLoading(true);
    const payload = { ...values };

    callApi({
      callback: record ? () => updateExam(payload, record?._id) : createExam,
      payload,
      onSuccess: ({ message }) => {
        ShowToast(message, Severty.SUCCESS);
        form.resetFields();
        refresh((x) => !x);
        setLoading(false);
        hide();
      },
      onError: (err) => {
        ShowToast(err, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  useApi({
    callback: getBelts,
    payload: selectedBranchId ? { branchId: selectedBranchId } : null,
    dependencies: [selectedBranchId],
    shouldCallApi: !!selectedBranchId,
    onSuccess: ({ data }) => {
      setBelts(data);
    },
  });

  useApi({
    callback: ListBranch,
    dependencies: [],
    shouldCallApi: true,
    onSuccess: ({ list }) => {
      const activeBranches = (list || []).filter((branch) => branch.isActive);
      setBranches(activeBranches);
    },
  });

  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        name: record?.name,
        beltId: record?.beltId?.map((item) => item?._id),
        branchId: record?.branchId?._id,
        date: moment(record?.date),
        description: record?.description,
        capacity: record?.capacity,
      });
      setSelectedBranchId(record?.branchId?._id);
    }
  }, [record, form]);

  return (
    <Modal
      title={record ? "Update Exam Schedule" : "Add Exam Schedule"}
      open={true}
      className="tab_modal"
      width={800}
      onCancel={hide}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            hide();
            form.resetFields();
          }}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={() => form.submit()}
          loading={loading}
          form="examForm"
        >
          Submit
        </Button>,
      ]}
    >
      <Form form={form} id="examForm" layout="vertical" onFinish={handleOk}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label="Exam Name"
              rules={[{ required: true, message: "Please enter exam name" }]}
            >
              <Input placeholder="Enter Exam Name" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="branchId"
              label="Branch"
              rules={[{ required: true, message: "Please select a branch" }]}
            >
              <Select placeholder="Select branch" onChange={(value) => {
                setSelectedBranchId(value);
              }}>
                {Array.isArray(branches) &&
                  branches.map((branch) => (
                    <Select.Option key={branch._id} value={branch._id}>
                      {branch.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="beltId"
              label="Belt"
              rules={[{ required: true, message: "Please select a belt" }]}
            >
              <Select mode="multiple" placeholder="Select belt">
                {Array.isArray(belts) &&
                  belts.map((belt) => (
                    <Select.Option key={belt._id} value={belt._id}>
                      {belt.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="date"
              label="Exam Date & Time"
              rules={[{ required: true, message: "Please select exam date" }]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: "100%" }}
                placeholder="Select Date & Time"
                disabledDate={(current) =>
                  current && current < moment().endOf("day")
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="capacity"
              label="Capacity"
            >
              <InputNumber style={{ width: "100%" }} placeholder="Enter Capacity" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: "Please enter description" }]}
            >
              <Input.TextArea placeholder="Enter Description" rows={3} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
